import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import productData from "../Datas/productData";
import "../css/main.scss";

function formatJustLandedPrice(price) {
  const num = Number.parseFloat(String(price), 10);
  if (Number.isNaN(num)) return price;
  return `£${Math.round(num)}`;
}

function Main() {
  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const justLanded = productData.slice(0, 5);

  const springBreakDressIds = [1, 6, 7, 8];
  const springBreakDresses = springBreakDressIds
    .map((id) => productData.find((p) => p.id === id))
    .filter(Boolean);

  const splitVideoRef = useRef(null);
  const [splitVideoPaused, setSplitVideoPaused] = useState(false);

  const toggleSplitVideo = () => {
    const el = splitVideoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setSplitVideoPaused(false);
    } else {
      el.pause();
      setSplitVideoPaused(true);
    }
  };

  return (
    <div className="main">
      <Header />

      <main>
        <section className="main-hero" aria-label="Spring collection">
          <div className="main-hero__inner">
            <div className="main-hero__content">
              <h1 className="main-hero__title">
                <span className="main-hero__title-line">Soak Up</span>
                <span className="main-hero__title-line">the Spring</span>
              </h1>
              <p className="main-hero__sub">
                Welcome to a new collection from the summer side of the season.
              </p>
              <div className="main-hero__ctas">
                <Link className="main-hero__btn" to="/shop" onClick={toTop}>
                  New for him
                </Link>
                <Link className="main-hero__btn" to="/shop" onClick={toTop}>
                  New for her
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section
          className="main-just-landed"
          aria-labelledby="main-just-landed-heading"
        >
          <h2 id="main-just-landed-heading" className="main-just-landed__title">
            Just Landed
          </h2>
          <div className="main-just-landed__row">
            {justLanded.map((product) => (
              <article
                key={product.id}
                className={
                  product.justLandedFeatured
                    ? "main-just-landed__card main-just-landed__card--featured"
                    : "main-just-landed__card"
                }
              >
                <Link
                  className="main-just-landed__link"
                  to={`/shopDetails/${product.id}`}
                  onClick={toTop}
                >
                  <div className="main-just-landed__img-wrap">
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                    />
                  </div>
                  <h3 className="main-just-landed__name">{product.name}</h3>
                  <p className="main-just-landed__price">
                    {formatJustLandedPrice(product.price)}
                  </p>
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section
          className="main-split-banner"
          aria-label="Dress shop promotion"
        >
          <div className="main-split-banner__half main-split-banner__half--left">
            <video
              ref={splitVideoRef}
              className="main-split-banner__video"
              autoPlay
              muted
              loop
              playsInline
              poster="/main/main3-1.webp"
              onPlay={() => setSplitVideoPaused(false)}
              onPause={() => setSplitVideoPaused(true)}
            >
              <source src="/main/main2-1.mp4" type="video/mp4" />
            </video>
            <button
              type="button"
              className="main-split-banner__pause"
              onClick={toggleSplitVideo}
              aria-label={splitVideoPaused ? "Play video" : "Pause video"}
            >
              {splitVideoPaused ? (
                <svg
                  className="main-split-banner__pause-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <polygon fill="currentColor" points="8,5 19,12 8,19" />
                </svg>
              ) : (
                <svg
                  className="main-split-banner__pause-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <rect x="7" y="5" width="3" height="14" fill="currentColor" />
                  <rect
                    x="14"
                    y="5"
                    width="3"
                    height="14"
                    fill="currentColor"
                  />
                </svg>
              )}
            </button>
          </div>
          <div className="main-split-banner__half main-split-banner__half--right">
            <div className="main-split-banner__overlay">
              <h2 className="main-split-banner__headline">
                <span className="main-split-banner__headline-line">
                  Dressed for
                </span>
                <span className="main-split-banner__headline-line">
                  a Getaway
                </span>
              </h2>
              <p className="main-split-banner__sub">
                Breezy new dresses that basically beg for a vacation.
              </p>
              <Link
                className="main-split-banner__btn"
                to="/shop"
                onClick={toTop}
              >
                The dress shop
              </Link>
            </div>
          </div>
        </section>

        <section
          className="main-spring-dresses"
          aria-labelledby="main-spring-dresses-heading"
        >
          <div className="main-spring-dresses__head">
            <h2
              id="main-spring-dresses-heading"
              className="main-spring-dresses__title"
            >
              The Spring Break Dresses
            </h2>
            <Link
              className="main-spring-dresses__view-all"
              to="/shop"
              onClick={toTop}
            >
              View all
            </Link>
          </div>
          <div className="main-spring-dresses__grid">
            {springBreakDresses.map((product) => (
              <article key={product.id} className="main-spring-dresses__card">
                <Link
                  className="main-spring-dresses__link"
                  to={`/shopDetails/${product.id}`}
                  onClick={toTop}
                >
                  <div className="main-spring-dresses__img-wrap">
                    {product.springBreakQuickView ? (
                      <span
                        className="main-spring-dresses__quick"
                        aria-hidden="true"
                      >
                        <svg
                          className="main-spring-dresses__quick-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.6"
                        >
                          <circle cx="11" cy="11" r="7" />
                          <path d="M20 20l-4-4" strokeLinecap="round" />
                        </svg>
                      </span>
                    ) : null}
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                    />
                  </div>
                  <h3 className="main-spring-dresses__name">{product.name}</h3>
                  <p className="main-spring-dresses__price">
                    {formatJustLandedPrice(product.price)}
                  </p>
                </Link>
              </article>
            ))}
          </div>
        </section>

        {/* 3 */}

        <section
          className="main-split-banner"
          aria-label="Dress shop promotion"
        >
          <div className="main-split-banner__half main-split-banner__half--right main-split-banner__half--right2">
            <div className="main-split-banner__overlay">
              <h2 className="main-split-banner__headline">
                <span className="main-split-banner__headline-line">
                  Dressed for
                </span>
                <span className="main-split-banner__headline-line">
                  a Getaway
                </span>
              </h2>
              <p className="main-split-banner__sub">
                Breezy new dresses that basically beg for a vacation.
              </p>
              <Link
                className="main-split-banner__btn"
                to="/shop"
                onClick={toTop}
              >
                The dress shop
              </Link>
            </div>
          </div>
          <div className="main-split-banner__half main-split-banner__half--left">
            <video
              ref={splitVideoRef}
              className="main-split-banner__video"
              autoPlay
              muted
              loop
              playsInline
              poster="/main/main3-1.webp"
              onPlay={() => setSplitVideoPaused(false)}
              onPause={() => setSplitVideoPaused(true)}
            >
              <source src="/main/main3-2.mp4" type="video/mp4" />
            </video>
            <button
              type="button"
              className="main-split-banner__pause"
              onClick={toggleSplitVideo}
              aria-label={splitVideoPaused ? "Play video" : "Pause video"}
            >
              {splitVideoPaused ? (
                <svg
                  className="main-split-banner__pause-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <polygon fill="currentColor" points="8,5 19,12 8,19" />
                </svg>
              ) : (
                <svg
                  className="main-split-banner__pause-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <rect x="7" y="5" width="3" height="14" fill="currentColor" />
                  <rect
                    x="14"
                    y="5"
                    width="3"
                    height="14"
                    fill="currentColor"
                  />
                </svg>
              )}
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default Main;
